import React, {Fragment} from 'react';
import {Col, Container, Row} from 'reactstrap';
import ReactMarkdown from 'react-markdown';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCartPlus} from '@fortawesome/pro-light-svg-icons';
import classNames from 'classnames';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {usePhaseState} from 'funnel-schedule/phase-context';
import {useUserState} from 'contexts/user';
import useBalanceText from 'utils/useBalanceText';

import SectionCL from 'components/ComponentLibrary/SectionCL';
import CheckoutButton from 'components/CheckoutButton';
import ProductImage from 'components/Elements/ProductImage';
import {SalesDisclaimer} from 'components/SalesCta';
import CountdownTimer from 'components/Elements/CountdownTimer';
import GuaranteeBox from 'components/Elements/Guarantee';
import IconListCL from 'components/ComponentLibrary/IconListCL/IconListCL';
import {ImgCardCol} from 'components/Elements/ImgBullet';
import {TestimonialBubble, TestimonialCircle} from 'components/Testimonial';
import FaqList from 'components/FaqList';
import {ListReferenceItem, ListReferences} from 'components/Elements/ListReference';

//@ts-ignore json error
import {playbookPlays, playbookPlaysAds, references, referencesSustainability} from 'data/ppp-info.json';
//@ts-ignore json error
import {bonusSession, bonusSessionAds} from 'data/bonus.json';
//@ts-ignore json error
import {faq, faqAds} from 'data/faq.json';
//@ts-ignore json error
import {recipeDefault} from 'data/recipe.json';

const remarkSubSuper = require('remark-sub-super');

export default function SalesCopy({
  version = 'sales',
  variant = null,
  bucketOverride = null,
  productId,
  productSku,
  productSlug,
  productPrice,
  paymentPlanPrice,
  salesFullPrice = false,
  ctaOverride = null,
  timerOverride = null,
  ctaNoTimer = false,
  ...props
}) {
  const {firstName, email} = useUserState();
  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before'
      },
      sales: salesSchedule = {
        phase: 'during'
      },
      webinar: webinarSchedule = {
        phase: 'before'
      }
    }
  } = usePhaseState();
  const {bucket: userBucket = []} = useUserState();
  const userSegment = bucketOverride ?? userBucket ?? 'health';
  const segment = userSegment === 'default' ? 'health' : userSegment;

  const salesEnd = new Date(webinarSchedule.endOfSalesDay * 1000).getTime();
  const salesTimerDate = timerOverride ? timerOverride : salesEnd;

  const screeningSalesPrice = 27;
  const joinSalesPrice = 47;
  const fullPrice = 197;
  const screeningPercent = 86;
  const joinSalesPercent = 76;

  function SectionSales({id, sectionHeading, angle = 'bottom', className = '', ...props}) {
    return (
      <SectionCL id={id} color="purple" angle={angle} className={className} type="triangle">
        <Container>
          <Row>
            <Col>
              {sectionHeading ? <div className="text-center text-white mb-5">{sectionHeading}</div> : null}
              <div className="background-white rounded text-center p-4 p-lg-5">
                {version === 'screening' ? (
                  <>
                    <h2 className="section-heading mb-2">Today Only</h2>
                    <h3 className="mt-0">
                      Enroll in the groundbreaking <span className="text-nowrap">Plant-Powered Playbook</span> for{' '}
                      {screeningPercent}% off
                    </h3>
                  </>
                ) : (
                  <h3 className="section-heading">
                    {!salesFullPrice ? (
                      <>
                        Enroll in the groundbreaking <span className="text-nowrap">Plant-Powered Playbook</span> for{' '}
                        {joinSalesPercent}% off
                      </>
                    ) : (
                      <>
                        Enroll in the groundbreaking <span className="text-nowrap">Plant-Powered Playbook</span>
                      </>
                    )}
                  </h3>
                )}
                <p>
                  <ProductImage maxHeight="500" />
                </p>
                {!salesFullPrice ? (
                  <>
                    <h4 className="mt-4 mb-3">
                      Normally <del>${fullPrice}</del>
                    </h4>
                    <h2 className="text-green m-0">
                      {version === 'screening' ? (
                        <>Masterclass Special: ${screeningSalesPrice}</>
                      ) : (
                        <>Now Only: ${joinSalesPrice}</>
                      )}
                    </h2>
                  </>
                ) : (
                  <h2 className="text-green m-0">Your Price: ${fullPrice}</h2>
                )}
                {
                  {
                    screening: null,
                    sales: (
                      <>
                        {!salesFullPrice ? (
                          <>
                            {ctaNoTimer ? null : (
                              <>
                                <div className="cta-timer mt-4 mb-3">
                                  <div className="offer-box-timer-text">Your discount expires in:</div>
                                  <CountdownTimer time={salesTimerDate} className="countdown-dark" />
                                </div>
                              </>
                            )}
                          </>
                        ) : null}
                      </>
                    )
                  }[version]
                }
                <CheckoutButton
                  sku={productSku}
                  price={productPrice}
                  slug={productSlug}
                  className="offer-box-button my-5"
                >
                  <FontAwesomeIcon className="mr-2" icon={faCartPlus} /> Buy Now
                </CheckoutButton>
                <p>
                  <b>Get instant access — even at 2 am – guaranteed!</b>
                </p>
                <SalesDisclaimer />
              </div>
            </Col>
          </Row>
        </Container>
      </SectionCL>
    );
  }

  function SectionOfferDream({color = 'white', imgPosition = 'left', ...props}) {
    return (
      <SectionCL
        id="offer-dream"
        color={color}
        imgPosition={imgPosition}
        //@ts-ignore cannot find module error
        imgUrl={require('static/backgrounds/1344525151.jpg')}
        type="triangle"
      >
        <h3 className="section-heading">
          {variant === 'ad' ? (
            <>Feeling better than ever, while contributing to a better world.</>
          ) : (
            <>Feeling better than you have in years while contributing to a better world.</>
          )}
        </h3>
        <p>That’s the DREAM.</p>
        <p>{variant === 'ad' ? <>Having energy again…</> : <>Having the energy to do all the things you love…</>}</p>
        <p>
          {variant === 'ad' ? (
            <>Eating the best foods for a long, healthy life…</>
          ) : (
            <>Knowing that you’re eating the best foods for a long, healthy life…</>
          )}
        </p>
        <p>All while giving the planet a huge helping hand and saving animals from needless suffering.</p>
      </SectionCL>
    );
  }

  function SectionResearch({color = 'light-gray', imgPosition = 'right', ...props}) {
    return (
      <SectionCL
        id="offer-research"
        color={color}
        imgPosition={imgPosition}
        //@ts-ignore cannot find module error
        imgUrl={require('static/backgrounds/1329039896.jpg')}
        type="triangle"
      >
        <h3 className="section-heading">The scientific research is in.</h3>
        <p>
          <b>
            {variant === 'ad' ? (
              <>A plant-based diet is one of the best things we can do for our health.</>
            ) : (
              <>A plant-based diet is one of the best things you can do for your health.</>
            )}
          </b>
        </p>
        <p>
          Fruits and vegetables provide us with antioxidants, phytochemicals, flavonoids, fiber, enzymes, and essential
          vitamins and minerals.
        </p>
        {variant === 'ad' ? (
          <>
            <p>
              A diet high in plant foods delivers prebiotic and probiotic goodness to gut microbes, which then reward us
              by producing short-chain fatty acids and other compounds crucial for good health.
            </p>
            <p>
              <b>Meanwhile, animal products have none of these benefits — and some are well-known carcinogens.</b>
            </p>
            <p>
              For everything from heart health to blood sugar, healthy weight to high-performance, plant-based diets are
              getting adopted by those in the know.
            </p>
            <p>Plant-strong athletes across many sports are busting myths and achieving unbelievable results.</p>
            <p>
              From weightlifting and bodybuilding to ultramarathon running, tennis, and the NFL, these top athletes are
              making the switch, winning, and feeling the difference.
            </p>
          </>
        ) : (
          <>
            <p>So it's no surprise that plant-based diets can:</p>
            <IconListCL>
              <>
                lower heart disease mortality<sup>1</sup>
              </>
              <>
                support sustainable weight management<sup>2</sup>
              </>
              <>
                reduce medication needs<sup>3-5</sup>
              </>
              <>
                lower the risk for most chronic diseases<sup>6-7</sup>
              </>
              <>
                possibly reverse advanced coronary artery disease<sup>9-10</sup> and type 2 diabetes.
                <sup>5</sup>
              </>
            </IconListCL>
          </>
        )}
        {
          {
            curious: (
              <p>Those are a lot of reasons to go plant-based. But protecting your health is just the beginning…</p>
            ),
            health: null
          }[segment]
        }
      </SectionCL>
    );
  }

  function SectionLoveEarth({color = 'white', imgPosition = 'left', ...props}) {
    return (
      <SectionCL
        id="offer-love-earth"
        color={color}
        imgPosition={imgPosition}
        //@ts-ignore cannot find module error
        imgUrl={require('static/backgrounds/1359773793.jpg')}
        type="triangle"
      >
        <h3 className="section-heading">If you love the Earth and animals, show it by how you eat!</h3>
        <p>This is it.</p>
        <p>We’re standing at the tipping point.</p>
        <p>We’ve all seen footage of what climate change is doing to our planet and its creatures, big and small.</p>
        <p>
          We’re already experiencing the effects in the form of extreme weather, hurricanes, fires, pollution,
          skyrocketing rates of disease, and dwindling natural resources like clean water…
        </p>
        <p>
          <b>And we know that how we’ve been doing things — especially with food — is just NOT sustainable.</b>
        </p>
        <p>Livestock from factory farms emit massive amounts of greenhouse gasses that pollute the air.</p>
        {segment === 'curious' && (
          <>
            <p>They pollute our clean water.</p>
            <p>These farms are the biggest source of wildlife extinction.</p>
            <p>And farm animals are subject to inhumane conditions.</p>
            <p>
              The evidence shows that{' '}
              <b>
                avoiding meat and dairy is the single biggest thing {variant === 'ad' ? 'we' : 'you'} can do to make an
                impact.
              </b>
            </p>
            <p>
              (Even more than driving an electric car, giving up showers, or turning off the air conditioner and the
              heater permanently!)
            </p>
          </>
        )}
        {segment === 'sustainability' && (
          <>
            <p>Industrialized agriculture is the biggest driver of rainforest destruction and wildlife extinction.</p>
            <p>And farm animals are subject to enormous cruelty.</p>
          </>
        )}
      </SectionCL>
    );
  }

  function SectionMakeDifference({color = 'light-gray', imgPosition = 'right', ...props}) {
    return (
      <SectionCL
        id="offer-make-difference"
        color={color}
        imgPosition={imgPosition}
        //@ts-ignore cannot find module error
        imgUrl={require('static/backgrounds/1199905534.jpg')}
        type="triangle"
      >
        <h3 className="section-heading">It’s NOT too late to make a difference.</h3>
        {
          {
            curious: (
              <>
                <p>There IS a solution — a sustainable way that just makes sense.</p>
                <h5 className="mt-0 mb-3">
                  But many people who try to go plant-based waste a lot of time and money — without getting all the
                  rewards.
                </h5>
                <p>{variant === 'ad' ? <>It’s unfortunate.</> : <>It’s unfortunate when you think about it.</>}</p>
                <p>
                  The majority of people who go plant-based are trying to do a good thing for their health, the planet,
                  and animals.
                </p>
                <p>
                  Except that without insider tips, going plant-based can backfire. (Just like with everything
                  worthwhile.)
                </p>
                <p>
                  {variant === 'ad' ? (
                    <>
                      That means missing out on the impressive health benefits like more energy, a strong immune system,
                      and great sleep.
                    </>
                  ) : (
                    <>
                      That means you’ll miss out on impressive health benefits, such as{' '}
                      <b>slashing the risk of heart disease, cancer, type 2 diabetes, and Alzheimer’s...</b> for more
                      energy, natural weight loss, and great sleep.
                    </>
                  )}
                </p>
                <p>
                  And you’ll skip one of the best opportunities to help our struggling planet and all the beautiful
                  creatures who live here.
                </p>
              </>
            ),
            sustainability: (
              <>
                <p>There IS a solution.</p>
                <p>
                  A <i>sustainable</i> way that just makes sense.
                </p>
                <h4 className="mt-0 mb-3">
                  The evidence shows that avoiding meat and dairy is the single biggest thing you can do to make an
                  impact.
                </h4>
                <p>
                  (Even more than driving an electric car, giving up showers, or turning off the air conditioner and the
                  heater permanently!)
                </p>
              </>
            )
          }[segment]
        }
      </SectionCL>
    );
  }

  function SectionQuestions({color = 'white', imgPosition = 'left', ...props}) {
    return (
      <SectionCL
        id="offer-questions"
        color={color}
        imgPosition={imgPosition}
        //@ts-ignore cannot find module error
        imgUrl={require('static/backgrounds/1360340739.jpg')}
        type="triangle"
      >
        <h5 className="section-heading">
          {
            {
              curious: (
                <>
                  Here’s the thing — when you’re just starting out, or even if you’ve been at it for a while, you might
                  have questions like,
                </>
              ),
              sustainability: (
                <>
                  Here’s the thing — it can be tricky to make the switch to a plant-based lifestyle <i>unless</i> you
                  get insider tips from seasoned pros.
                </>
              ),
              health: <>Get insider tips from seasoned plant-based pros.</>
            }[segment]
          }
        </h5>
        {
          {
            curious: (
              <>
                <p>“What do I eat instead of meat and dairy?”</p>
                <p>“How will I get enough protein?”</p>
                <p>
                  “I know there are a few supplements I’ll need, but I have no idea if I’m buying the right thing or
                  just kissing my money away with nothing to show for it.”
                </p>
              </>
            ),
            sustainability: (
              <>
                <p>You might wonder things like:</p>
                <p>“How can I go plant-based without getting sick or feeling tired all the time?”</p>
                <p>“Will I get enough protein?”</p>
                <p>
                  “I know there are a few supplements I’ll want, but I have no idea if I’m buying the right thing or
                  just kissing my money away with nothing to show for it.”
                </p>
              </>
            ),
            health: (
              <>
                {variant === 'ad' ? (
                  <p>Wondering things like…</p>
                ) : (
                  <>
                    <p>
                      If you’re thinking about switching to a plant-based diet or want to do better at it, you might
                      wonder things like:
                    </p>
                  </>
                )}
                <p>“What will I eat?”</p>
                <p>“Will I get enough protein?”</p>
                <p>
                  “I know there are a few supplements I’ll need, but I have no idea if I’m buying the right thing or
                  just kissing my money away with nothing to show for it.”
                </p>
              </>
            )
          }[segment]
        }

        <p>
          “What in the name of tofu will I do when I’m at a family gathering with a bunch of enthusiastic carnivores?”
        </p>
        <p>“Am I actually going to be able to — gasp! — stop eating cheese?”</p>
        {variant === 'ad' && segment === 'health' ? null : (
          <p>
            And the last thing you want is to make a big declaration to yourself and others that, “I’M DOING IT!” ...
            only to slip back to your old ways a few weeks later
            {segment === 'health' ? '.' : <>, feeling foolish (and feeling guilty for the planet, too!).</>}
          </p>
        )}
        <h5 className="mt-0">
          <i>We’ve got you.</i>
        </h5>
      </SectionCL>
    );
  }

  useBalanceText();
  return (
    <>
      {version === 'screening' ? (
        <SectionSales
          id="offer-cta-1"
          sectionHeading={
            <>
              <h3 className="section-heading mb-2">One-time offer</h3>
              <p className="mb-1">Special discount is only available on this page.</p>
              <p className="mb-0">
                <i>Please do not close or refresh this page.</i>
              </p>
            </>
          }
        />
      ) : null}

      <SectionCL
        id=""
        color={version === 'screening' ? 'black' : 'purple'}
        className="text-center text-white"
        type="triangle"
      >
        <Container>
          <Row>
            <Col>
              <h3
                className={classNames(
                  `section-heading ${
                    (variant === 'ad' && segment === 'curious') || (variant === 'ad' && segment === 'health')
                      ? 'mb-0'
                      : ''
                  }`
                )}
              >
                {firstName ? `${firstName}, ` : null}
                {
                  {
                    curious: (
                      <>
                        {variant === 'ad' ? (
                          <>
                            Join the movement of millions of people who are reaping all the benefits of a plant-powered
                            lifestyle!
                          </>
                        ) : (
                          <>It’s time to make a massive difference for your health, for animals, and for the planet.</>
                        )}
                      </>
                    ),
                    sustainability: (
                      <>
                        {variant === 'ad' ? (
                          <>Going plant-based is the hands down best thing we can do for the planet.</>
                        ) : (
                          <>Going plant-based is the hands-down best thing you can do for the planet.</>
                        )}
                      </>
                    ),
                    health: (
                      <>
                        {variant === 'ad' ? (
                          <>
                            Join the movement of millions of people who are reaping all the benefits of a plant-powered
                            lifestyle!
                          </>
                        ) : (
                          <>It’s time to make a massive difference in your health.</>
                        )}
                      </>
                    )
                  }[segment]
                }
              </h3>
              <p className="balance-text">
                {
                  {
                    curious: (
                      <>
                        {variant === 'ad' ? null : (
                          <>
                            Join the movement of millions of people who are reaping all the benefits of a plant-powered
                            lifestyle!
                          </>
                        )}
                      </>
                    ),
                    sustainability: (
                      <>
                        Join the movement of millions of people who are showing they care — with a plant-powered
                        lifestyle.
                      </>
                    ),
                    health: (
                      <>
                        {variant === 'ad' ? null : (
                          <>
                            Join the movement of millions of people who are reaping the health benefits of a
                            plant-powered lifestyle!
                          </>
                        )}
                      </>
                    )
                  }[segment]
                }
              </p>
            </Col>
          </Row>
        </Container>
      </SectionCL>

      {
        {
          curious: (
            <>
              <SectionOfferDream />
              <SectionResearch />
              <SectionLoveEarth />
              <SectionMakeDifference />
              <SectionQuestions />
            </>
          ),
          sustainability: (
            <>
              <SectionLoveEarth />
              <SectionMakeDifference />
              <SectionQuestions />
              <SectionCL
                id="offer-powerful-choices"
                color="light-gray"
                imgPosition="right"
                //@ts-ignore cannot find module error
                imgUrl={require('static/backgrounds/1407355865.jpg')}
                type="triangle"
              >
                <Container>
                  <Row>
                    <Col>
                      <p>No more overwhelming information (or misinformation!).</p>
                      <p>No more anxiety about making the “right” decision.</p>
                      <p>
                        No more wondering what to eat to get{variant === 'ad' ? null : ' your'} nutritional needs met.
                      </p>
                      <p>
                        No more falling victim to greenwashing… where companies make their products look eco-friendly
                        just so you’ll buy them.
                      </p>
                      <p>
                        <b>
                          Instead, you can make powerful choices that help you, your community, animals, and the planet.
                          Future generations will be forever thankful{variant === 'ad' ? null : ' to you'}.
                        </b>
                      </p>
                    </Col>
                  </Row>
                </Container>
              </SectionCL>
              <SectionCL
                id="offer-care"
                color="white"
                imgPosition="left"
                //@ts-ignore cannot find module error
                imgUrl={require('static/backgrounds/1333170592.jpg')}
                type="triangle"
              >
                <Container>
                  <Row>
                    <Col>
                      <h4 className="section-heading">If you:</h4>
                      <p>Care about the planet… so deeply that sometimes it’s too heartbreaking to think about…</p>
                      <p>
                        {variant === 'ad' ? (
                          <>Want to make good choices and sleep well at night…</>
                        ) : (
                          <>Want to make choices you feel good about so you can sleep well at night…</>
                        )}
                      </p>
                      <p>
                        {variant === 'ad' ? (
                          <>Want to eat food that’s healthy…</>
                        ) : (
                          <>
                            Want to be confident that you’re eating food that’s as healthy for you as it is good for the
                            world…
                          </>
                        )}
                      </p>
                      <p>
                        And you know that the time to act is NOW — that we can’t wait to create a sustainable planet —
                        because “too late” is right around the corner…
                      </p>
                      {variant === 'ad' ? null : (
                        <p>
                          <b>Then you might already know that food is at the core of creating a sustainable world.</b>
                        </p>
                      )}
                    </Col>
                  </Row>
                </Container>
              </SectionCL>
            </>
          ),
          health: (
            <>
              <SectionOfferDream color={variant === 'ad' ? 'light-gray' : 'white'} />
              <SectionResearch color={variant === 'ad' ? 'white' : 'light-gray'} />
              <SectionCL
                id="offer-rewards"
                color={variant === 'ad' ? 'light-gray' : 'white'}
                imgPosition="left"
                //@ts-ignore cannot find module error
                imgUrl={require('static/backgrounds/1337844041.jpg')}
                type="triangle"
              >
                <h5 className="section-heading">
                  But many people who try to go plant-based waste a lot of time and money — without getting all the
                  rewards.
                </h5>
                <p>{variant === 'ad' ? <>It’s unfortunate.</> : <>It’s unfortunate when you think about it.</>}</p>
                <p>
                  The majority of people who go plant-based are trying to do a good thing for their health (and for the
                  planet and animals).
                </p>
                <p>
                  Except that, without getting the foundations right, it can backfire… Just like with almost anything
                  worthwhile.
                </p>
              </SectionCL>
              <SectionCL
                id="offer-benefits"
                color={variant === 'ad' ? 'white' : 'light-gray'}
                imgPosition="right"
                //@ts-ignore cannot find module error
                imgUrl={require('static/backgrounds/923778358.jpg')}
                type="triangle"
              >
                <p>
                  {variant === 'ad' ? (
                    <>That means you could miss out on the impressive health benefits like:</>
                  ) : (
                    <>
                      That means you could miss out on the impressive health benefits like{' '}
                      <b>slashing the risk of heart disease, cancer, type 2 diabetes, or Alzheimer’s.</b>
                    </>
                  )}
                </p>
                {variant === 'ad' ? null : <p>And you might never experience other benefits like:</p>}
                <IconListCL>
                  <>Increased energy</>
                  <>Excellent sleep</>
                  <>Improved digestion</>
                  <>A strong immune system</>
                  <>Getting and staying at a healthy weight</>
                </IconListCL>
              </SectionCL>
              <SectionCL
                id="offer-want-to"
                color={variant === 'ad' ? 'light-gray' : 'white'}
                imgPosition="left"
                //@ts-ignore cannot find module error
                imgUrl={require('static/backgrounds/1319314684.jpg')}
                type="triangle"
              >
                <p>
                  {variant === 'ad' ? (
                    <>Every time we sit down to eat, the same question is there, whether you ask it or not…</>
                  ) : (
                    <>
                      Every time you sit down to eat, the same question is there, whether you ask it to yourself or not…
                    </>
                  )}
                </p>
                <p>
                  <b>“Am I eating food that’s going to make my body stronger or deplete it?”</b>
                </p>
                <p>
                  It’s an important question because what you eat has the power to transform how you feel, which genes
                  get expressed, and whether or not {variant === 'ad' ? 'we' : 'you'} get certain diseases.
                </p>
                <p>
                  <b>It’s too important to leave it to chance.</b>
                </p>
                <p>Especially if you want to:</p>
                <p>
                  {variant === 'ad' ? (
                    <>Avoid today’s all-too-common diseases that could be directly linked to food.</>
                  ) : (
                    <>Avoid today’s all-too-common diseases that could be directly linked to the food on your plate.</>
                  )}
                </p>
                <p>Be a role model for your family and others you care about.</p>
                <p>
                  {variant === 'ad' ? (
                    <>
                      Age vibrantly (and spend time doing things you love, rather than living in fear of doctor’s
                      visits?)
                    </>
                  ) : (
                    <>
                      Age vibrantly (wouldn’t you rather spend your time doing things you love, rather than living in
                      fear of doctor’s visits and having your mobility restricted?)
                    </>
                  )}
                </p>
                <p>
                  Ditch medications that become unnecessary — thanks to making more informed choices about what to eat.
                </p>
              </SectionCL>
              {variant === 'ad' ? null : (
                <SectionCL
                  id="offer-never-late"
                  color="light-gray"
                  imgPosition="right"
                  //@ts-ignore cannot find module error
                  imgUrl={require('static/backgrounds/1334338722.jpg')}
                  type="triangle"
                >
                  <h5 className="section-heading">
                    But when you look at what you’re eating on a regular basis, are you sure your food is putting you on
                    the path to health? Or are you second-guessing yourself?
                  </h5>
                  <p>If only knowing that you could be eating a little better was the worst of it...</p>
                  <p>
                    The worst of it is looking back and wishing you could go back in time to eat foods that kept you
                    healthy. But that’s not going to happen to you. You can never go back, and sometimes, there is such
                    a thing as being “too late.”
                  </p>
                  <p>
                    If you’re reading this, and you still have breath in your lungs, blood in your veins, and hope in
                    your heart, then I’m here to tell you it isn’t too late
                    {firstName ? <>for you, {firstName}.</> : '.'}
                  </p>
                  <p>It’s never too late to take action and reap the benefits of a new way of eating and living.</p>
                </SectionCL>
              )}
              <SectionQuestions imgPosition={variant === 'ad' ? 'right' : 'left'} />
            </>
          )
        }[segment]
      }

      <SectionCL id="offer-experience" color="light-gray" type="triangle">
        <Container>
          <Row>
            <Col>
              <h3 className="section-heading text-center">
                {
                  {
                    curious: (
                      <>
                        {variant === 'ad' ? (
                          <>
                            Imagine knowing <i>exactly</i> what to eat to get a health breakthrough.
                          </>
                        ) : (
                          <>
                            Imagine knowing <i>exactly</i> what to eat to get your own health breakthrough.
                          </>
                        )}
                      </>
                    ),
                    sustainability: (
                      <>Imagine feeling great about what’s on your plate each time you sit down to eat.</>
                    ),
                    health: <>Learn exactly what to eat to get your own health breakthrough.</>
                  }[segment]
                }
              </h3>
              {variant === 'ad' ? (
                <>
                  {
                    {
                      curious: null,
                      sustainability: (
                        <>
                          <p className="text-center balance-text">
                            To eat food that’s going to bring energy, joy, and vitality.
                          </p>
                          <p className="text-center balance-text mb-4">
                            And imagine how GOOD it will feel knowing that you’re making the world a better place,
                            simply with what you eat for breakfast, lunch, and dinner.
                          </p>
                        </>
                      ),
                      health: null
                    }[segment]
                  }
                </>
              ) : (
                <>
                  <p className="text-center balance-text">
                    {
                      {
                        curious: (
                          <>
                            To know that you’re eating food that’s going to bring you energy, joy, and vitality —
                            instead of food that can drag you down, weaken your immune system, or leave you prone to
                            diseases.
                          </>
                        ),
                        sustainability: (
                          <>
                            To know that you’re making the world a better place simply with what you eat for breakfast,
                            lunch, and dinner.
                          </>
                        ),
                        health: (
                          <>
                            To know that you’re eating food that’s going to bring you energy, joy, and vitality —
                            instead of food that can drag you down, weaken your immune system, or leave you prone to
                            diseases.
                          </>
                        )
                      }[segment]
                    }
                  </p>
                  <p className="text-center balance-text">
                    {
                      {
                        curious: (
                          <>
                            And imagine how GOOD it will feel knowing that you’re making the world a better place,
                            simply with what you eat for breakfast, lunch, and dinner.
                          </>
                        ),
                        sustainability: (
                          <>
                            Eat food that will bring you energy, joy, and vitality… instead of dragging you down,
                            weakening your immune system, or leaving you prone to diseases.
                          </>
                        ),
                        health: (
                          <>
                            And imagine how GOOD it will feel knowing that you’re making the world a better place,
                            simply with what you eat for breakfast, lunch, and dinner.
                          </>
                        )
                      }[segment]
                    }
                  </p>
                  <p className="text-center mb-4">
                    <b>This is an experience you cannot miss.</b>
                  </p>
                </>
              )}
              <ImgCardCol
                //@ts-ignore cannot find module error
                img={require('static/backgrounds/854725372-wide.jpg')}
                imgColLg="5"
                textColLg="7"
              >
                <h4 className="section-only-heading">
                  <i>Eat delicious food</i>
                </h4>
              </ImgCardCol>
              <ImgCardCol
                //@ts-ignore cannot find module error
                img={require('static/backgrounds/1265315806-wide.jpg')}
                imgColLg="5"
                textColLg="7"
              >
                <h4 className="section-only-heading">
                  <i>
                    {segment === 'sustainability' ? (
                      <>Be kind to the planet and to animals</>
                    ) : (
                      <>Get the best health</>
                    )}
                  </i>
                </h4>
              </ImgCardCol>
              <ImgCardCol
                //@ts-ignore cannot find module error
                img={require('static/backgrounds/1327919661-wide.jpg')}
                imgColLg="5"
                textColLg="7"
              >
                <h4 className="section-only-heading">
                  <i>
                    {segment === 'sustainability' ? (
                      <>Get the best health</>
                    ) : (
                      <>Be kind to the planet and to animals</>
                    )}
                  </i>
                </h4>
              </ImgCardCol>

              {
                {
                  curious: (
                    <>
                      {variant === 'ad' ? null : (
                        <h5 className="text-uppercase text-center mt-4">
                          If you want excellent health and to do your part for the planet, animals, and future
                          generations, this is for you.
                        </h5>
                      )}
                    </>
                  ),
                  sustainability: (
                    <>
                      {variant === 'ad' ? (
                        <h5 className="text-uppercase text-center mt-4">
                          Preserve Our Planet Today and For Future Generations.
                        </h5>
                      ) : (
                        <h5 className="text-uppercase text-center mt-4">
                          If you want to preserve our planet today and for future generations, and love your health and
                          your life, this is for you.
                        </h5>
                      )}
                    </>
                  ),
                  health: (
                    <>
                      {variant === 'ad' ? null : (
                        <h5 className="text-uppercase text-center mt-4">
                          If you want excellent health and to do your part for the planet, animals, and future
                          generations, this is for you.
                        </h5>
                      )}
                    </>
                  )
                }[segment]
              }
            </Col>
          </Row>
        </Container>
      </SectionCL>

      <SectionCL id="offer-hello" color="white" className="text-center" type="triangle">
        <Container>
          <Row className="justify-content-center">
            <Col lg="10">
              <h3 className="section-heading mb-3">Say Hello to the Plant-Powered Playbook</h3>
              <h4 className="mt-0">
                <i>A Beginner's Guide to Plant-Based Living</i>
              </h4>
              <p className="text-16">
                <i>(Please note: This product is 100% digital and downloadable)</i>
              </p>
              <ProductImage />
              <p>
                The Plant-Powered Playbook is the premier guide to going plant-based, the smart way… without hiccups,
                setbacks, costly mistakes, or resistance.
              </p>
              <p>
                Go behind the scenes with plant-based experts who know all the tips and tricks to see exactly how
                they’re doing it — so you can do it, too.
              </p>
              <p>
                Get in-depth insights into nutrition, protein, and how to avoid becoming a junk food vegan who{' '}
                <i>isn’t</i> doing their health any favors.
              </p>
              {variant === 'ad' ? null : (
                <p>
                  (This is one of the most critical lessons of all, especially since the boom in plant-based interest
                  has given us more options than ever. But just because something is vegan doesn’t make it good for you.
                  Here’s looking at <i>you,</i> French fries!)
                </p>
              )}
            </Col>
          </Row>
        </Container>
      </SectionCL>

      <SectionCL
        id="offer-ocean-1"
        color="light-gray"
        imgPosition="left"
        imgUrl="https://cdn.foodrevolution.org/global/team/frn-guest_expert-portrait-square-johnoceanrobbins-opt.jpg"
        type="triangle"
        angleBottomDisplay="none"
        innerClass="pb-0"
      >
        <h3 className="section-heading">Hi, I’m Ocean Robbins.</h3>
        <p>
          Along with my dad and colleague, John Robbins, one of the original rebels who kicked off the plant-based way
          of life for millions of people, I cofounded the Food Revolution Network to ignite a movement for healthy,
          sustainable food for all — one that includes a humane life for animals and protecting the environment, too.
        </p>
        <p>
          After decades of leading the charge to help people discover the exquisite benefits of a plant-based lifestyle,
          we’re extremely pleased to see its popularity skyrocket.
        </p>
        <p>
          That’s <i>excellent</i> news.
        </p>
      </SectionCL>
      <SectionCL id="offer-ocean-2" color="light-gray" type="triangle" innerClass="sectionCL-padding-none">
        <Container>
          <Row>
            <Col>
              <p>
                It means that millions of people are waking up and becoming more conscious of how their daily actions
                affect the world around them.
              </p>
              <p>
                It means fewer greenhouse gases pumped into the atmosphere from livestock, a reduction in global
                warming, and less pollution, erosion, runoff, and wasted water!
              </p>
              <p>
                It means that millions of animals’ lives will be spared, with fewer animals raised in toxic conditions,
                force-fed hormones, and antibiotics in a system that’s so wildly inefficient.
              </p>
              <p>
                And it means entire families are changing their fate by avoiding foods that harm their bodies, raise
                cholesterol, clog arteries, and cause chronic disease and premature aging.
              </p>
              <p>
                <b>But we can’t wait.</b>
              </p>
              <p>
                <b>Every meal counts.</b>
              </p>
              <p>
                We’d love nothing more than to walk side-by-side with you on one of the most important missions in the
                history of the planet.
              </p>
            </Col>
          </Row>
        </Container>
      </SectionCL>

      <SectionCL
        id="offer-nichole-1"
        color="white"
        imgPosition="right"
        imgUrl="https://cdn.foodrevolution.org/global/team/nichole-d.jpg"
        type="triangle"
        angleBottomDisplay="none"
        innerClass="pb-0"
      >
        <h3 className="section-heading">Hi, I’m Nichole,</h3>

        <p>
          I’m Food Revolution Network’s Dietitian and Recipe Developer. I can’t wait to show you the ins and outs of
          going plant-based.
        </p>
        <p>
          Years ago, my life changed when I learned the truth about dairy factory farms and their impact on animals and
          the planet. I truly had no idea how the dairy on my plate arrived there. It was one of the biggest eye-opening
          moments of my life, and it changed my life forever in the very best way.
        </p>
        <p>
          {variant === 'ad' ? (
            <>
              That night I decided to go 100% plant-based. The dietitian in me needed to research further to make sure
              this type of diet was sustainable and healthy if I was planning to recommend it to the public. Every day
              that I researched I was more and more amazed at all of the evidence showcasing the power of plants for
              optimal health — from reducing the risk of lifestyle diseases to improving everyday quality of life
              (better sleep, boosted mood, more energy, and so much more!). I couldn’t believe the amount of research
              that supported plant-based eating and that I hadn’t been recommending this way of eating for my entire
              career!
            </>
          ) : (
            <>
              That night I decided to go 100% plant-based. The dietitian in me needed to research further to make sure
              this type of diet was sustainable and healthy if I was planning to recommend it to the public. Every day
              that I researched, I was more and more amazed at all of the evidence showcasing the power of plants for
              optimal health — from reducing the risk of lifestyle diseases like heart disease and cancer to improving
              everyday quality of life (better sleep, boosted mood, more energy, and so much more!). I couldn’t believe
              the amount of research that supported plant-based eating and that I hadn’t been recommending this way of
              eating for my entire career!
            </>
          )}
        </p>
      </SectionCL>
      <SectionCL id="offer-nichole-2" color="white" type="triangle" innerClass="sectionCL-padding-none">
        <Container>
          <Row>
            <Col>
              <p>
                I noticed that my own quality of life improved. In fact, I hadn’t even realized it needed improvement
                until I went plant-based and found that:
              </p>
              <p>
                I slept better.
                <br />
                My skin health improved.
                <br />
                My digestion improved.
                <br />I had boundless energy.
              </p>
              <p>
                From that day forward, I made it my mission to share my personal experience in going plant-based and all
                of the incredible research that supports eating plants for better health… health for humans, the planet,
                and animal welfare.
              </p>
              <p>
                Of course, the key isn’t just knowing what to do. It’s doing what you know! And that’s why we are so
                proud of the Plant-Powered Playbook.
              </p>
            </Col>
          </Row>
        </Container>
      </SectionCL>

      <SectionCL id="offer-join-us" color="black" className="text-center text-white" type="triangle">
        <Container>
          <Row>
            <Col>
              <h2 className="section-heading mb-3">So, will you join us?</h2>
              <h4 className="text-400 m-0">
                It’s going to be so much fun — and <i>so</i> delicious!
              </h4>
            </Col>
          </Row>
        </Container>
      </SectionCL>

      <SectionCL id="offer-testimonials-1" color="light-gray" type="triangle">
        <Container>
          <Row>
            <Col>
              <TestimonialCircle id="paul-mccartney" />
              <hr className="my-5 border-color-black-25" />
              <TestimonialCircle id={variant === 'ad' ? 'michael-greger-ads' : 'michael-greger'} imgPosition="right" />
            </Col>
          </Row>
        </Container>
      </SectionCL>

      <SectionCL id="offer-testimonials-2" color="white" type="triangle">
        <Container>
          <Row>
            <Col>
              <h2 className="section-heading text-center text-uppercase">
                Here’s What Food Revolution Network’s Members Say About Eating Plant-Based Whole Foods
              </h2>
              <p className="balance-text text-center mb-5">
                These stories are from people who’ve found success with our programs. Each of them is unique. And so are
                you! Your results will be specific to you, of course. And no, we never offer compensation for these
                statements!
              </p>
              <TestimonialBubble id={variant === 'ad' ? 'donna-b-ads' : 'donna-b'} className="mb-5" />
              {variant === 'ad' ? null : <TestimonialBubble id="eric" className="mb-5" />}
              <TestimonialBubble id={variant === 'ad' ? 'margaret-s-ads' : 'margaret-s'} className="mb-5" />
              <TestimonialBubble id="terri-c" className="mb-5" />
              <TestimonialBubble id="kari" className="mb-5" />
              <TestimonialBubble id={variant === 'ad' ? 'cheryl-ads' : 'cheryl'} className="mb-5" />
              <TestimonialBubble id={variant === 'ad' ? 'ingrid-ads' : 'ingrid'} className="mb-5" />
              <TestimonialBubble id="marium" className="mb-5" />
              <TestimonialBubble id={variant === 'ad' ? 'robin-ads' : 'robin'} className="mb-5" />
              <TestimonialBubble id={variant === 'ad' ? 'nerina-ads' : 'nerina'} />
              <h4 className="text-center mb-3">
                These are just a tiny fraction of the phenomenal wins we’ve seen when people adopt a whole foods,
                plant-based lifestyle.
              </h4>
              <h4 className="text-center mt-0">
                What wins will <i>you</i> experience{firstName && `, ${firstName}`}?
              </h4>
            </Col>
          </Row>
        </Container>
      </SectionCL>

      <SectionCL id="offer-works" color="green" className="text-center text-white" type="triangle">
        <Container>
          <Row>
            <Col>
              <h2 className="section-only-heading">How It Works</h2>
            </Col>
          </Row>
        </Container>
      </SectionCL>

      <SectionCL id="offer-works-playbook" color="light-gray" type="triangle">
        <Container>
          <Row>
            <Col>
              <h3 className="section-heading text-center mb-3">Get your Plant-Powered Playbook</h3>
              <p className="text-center text-16">
                <i>(Please note: This product is 100% digital and downloadable)</i>
              </p>
              <p className="text-center">
                <img
                  src="https://cdn.foodrevolution.org/ppp/ppp-cover-mockup-20221012.png"
                  style={{maxHeight: '500px'}}
                />
              </p>
              <p>
                {variant === 'ad' ? (
                  <>
                    Each of the 15 chapters addresses one of the top problems that plant-based eaters face — and gives a
                    simple and effective “play” to solve it. Find out exactly how to crush a plant-powered lifestyle.
                  </>
                ) : (
                  <>
                    Each of the 15 chapters addresses one of the top problems that plant-based eaters face — and gives
                    you a simple and effective “play” to solve it. You’ll find out exactly how to crush a plant-powered
                    lifestyle.
                  </>
                )}
              </p>
            </Col>
          </Row>
        </Container>
      </SectionCL>
      <SectionCL id="offer-works-tutorials" color="white" type="triangle">
        <Container>
          <Row>
            <Col>
              <h3 className="section-heading text-center">Watch 5-Minute Video Tutorials</h3>
              <p className="text-center">
                <img
                  //@ts-ignore cannot find module error
                  src={require('static/ppp-sales-page-image-videotutorial.jpg')}
                  style={{maxHeight: '400px'}}
                />
              </p>
              <p>
                {variant === 'ad' ? (
                  <>
                    All 15 plays come with a lively video lesson for a quick dive into the wonderful world of
                    plant-based dos… and don’ts. Just click the link, or snap a pic with your smartphone, to access the
                    videos and get fired up.
                  </>
                ) : (
                  <>
                    All 15 plays come with a lively video lesson for a quick dive into the wonderful world of
                    plant-based dos… and don’ts. Just click the link or snap a picture with your smartphone to access
                    the videos and get fired up about your easiest path to massive success.
                  </>
                )}
              </p>
            </Col>
          </Row>
        </Container>
      </SectionCL>
      <SectionCL id="offer-works-demos" color="light-gray" type="triangle">
        <Container>
          <Row>
            <Col>
              <h3 className="section-heading text-center">Kitchen Demos with a Top Plant-Based Dietitian</h3>
              <p className="text-center mb-4">
                <img
                  //@ts-ignore cannot find module error
                  src={require('static/ppp-recipe-demo-screenshot.jpg')}
                  style={{maxHeight: '400px'}}
                />
              </p>
              <p>
                {variant === 'ad' ? (
                  <>
                    Learn by watching a master nutritionist working her genius in the kitchen! Get insider tips to save
                    time and avoid common cooking mistakes while whipping up your new favorite dishes.
                  </>
                ) : (
                  <>
                    Learn by watching a master nutritionist working her genius in the kitchen! You’ll get insider tips
                    to save time and avoid common cooking mistakes while whipping up your new favorite dishes.
                  </>
                )}
              </p>
            </Col>
          </Row>
        </Container>
      </SectionCL>

      <SectionCL id="offer-works-recipes" color="white" type="triangle">
        <Container>
          <Row>
            <Col>
              <h3 className="section-heading text-center">15 Plant-Based Recipes to Enjoy Immediately</h3>
              <p className="text-center mb-4">
                <img
                  //@ts-ignore cannot find module error
                  src={require('static/ppp-recipe-photo-collage.png')}
                  style={{maxHeight: '500px'}}
                />
              </p>
              <p>
                The surefire way to start off strong (and stay there!) is with a thick stack of mouthwatering recipes.
                Best yet, you’ll get the four sauces to rule them all for tasty dishes that come together in minutes.
              </p>
            </Col>
          </Row>
        </Container>
      </SectionCL>

      <SectionCL id="offer-works-lessons" color="light-gray" type="triangle">
        <Container>
          <Row>
            <Col>
              <h3 className="section-heading text-center">Lessons and Action Steps Delivered Direct</h3>
              <p className="text-center mb-4">
                <img
                  //@ts-ignore cannot find module error
                  src={require('static/ppp-chapter-sample-display.png')}
                  style={{maxHeight: '500px'}}
                />
              </p>
              <p>
                {variant === 'ad' ? (
                  <>
                    Plant-Powered Playbook lessons and action steps get emailed over the course of 15 days. Within a
                    couple of weeks, you’ll be making new choices and reaping the abundant rewards. Easily and
                    deliciously!
                  </>
                ) : (
                  <>
                    Plant-Powered Playbook lessons and action steps get emailed to you over the course of 15 days.
                    Within a couple of weeks, you’ll be making new choices and reaping the abundant rewards. Easily and
                    deliciously!
                  </>
                )}
              </p>
            </Col>
          </Row>
        </Container>
      </SectionCL>

      <SectionCL id="offer-works-bonus" color="white" type="triangle">
        <Container>
          <Row>
            <Col>
              <h3 className="section-heading text-center">Bonus! Four Sessions with Plant-Based Lifestyle Experts</h3>
              <p className="text-center m-0">
                <img
                  //@ts-ignore cannot find module error
                  src={require('static/bonus-content-badge.png')}
                  style={{maxHeight: '400px'}}
                />
              </p>
              <p>
                {variant === 'ad' ? (
                  <>
                    Experience a true health transformation! Get critical insights from some of the world’s top health
                    experts. If you want to dig deeper, learn more, or get reinspired, simply listen to these four
                    wildly popular and brilliant interviews from the famous Food Revolution Summits.
                  </>
                ) : (
                  <>
                    Experience a true health transformation! Get critical insights from some of the world’s top health
                    experts. If you want to dig deeper, learn more, or get reinspired, you’ll love listening to these
                    four wildly popular and brilliant interviews from the famous Food Revolution Summits.
                  </>
                )}
              </p>
            </Col>
          </Row>
        </Container>
      </SectionCL>

      <SectionCL id="offer-plays" color="light-gray" type="triangle">
        <Container>
          <Row>
            <Col>
              <h2 className="section-heading text-center">The 15 Plant-Powered Plays</h2>
              {variant === 'ad' ? (
                <>
                  {playbookPlaysAds.map((item, i) => (
                    <ImgCardCol img={item.image} className="mb-4" key={`pppPlay-${i}`}>
                      <h5 className="section-heading">
                        {i + 1}. {item.title}
                      </h5>
                      <ReactMarkdown source={item.description} />
                    </ImgCardCol>
                  ))}
                </>
              ) : (
                <>
                  {playbookPlays.map((item, i) => (
                    <ImgCardCol img={item.image} className="mb-4" key={`pppPlay-${i}`}>
                      <h5 className="section-heading">
                        {i + 1}. {item.title}
                      </h5>
                      <ReactMarkdown source={item.description} />
                    </ImgCardCol>
                  ))}
                </>
              )}
            </Col>
          </Row>
        </Container>
      </SectionCL>

      <SectionCL id="offer-food" color="white" type="triangle">
        <Container>
          <Row>
            <Col>
              <h2 className="section-heading text-uppercase text-center">THE FOOD!</h2>
              <p className="balance-text text-center mb-5">
                The <i>Plant-Powered Playbook</i> gives you scrumptious recipes to kick off a new culinary adventure
                (the easy way).
              </p>
            </Col>
          </Row>
          <Row className="d-flex align-items-stretch justify-content-center">
            {recipeDefault.map((recipe, i) => (
              <Col md="6" xl="4" className="d-flex flex-grow mb-4" key={`recipe-${i}`}>
                <div className="box-shadow-black-50 rounded">
                  <img className="rounded-top" src={recipe.image} />
                  <div className="p-4 rounded-bottom">
                    <h5 className="section-heading text-center">{recipe.name}</h5>
                    <ReactMarkdown
                      className="offer-food-recipe-description text-16"
                      source={recipe.description || 'recipe description'}
                      plugins={[remarkSubSuper]}
                      renderers={{
                        sub: 'sub',
                        sup: 'sup'
                      }}
                    />
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </SectionCL>

      <SectionCL
        id="offer-learn-1"
        color="light-gray"
        imgPosition="left"
        //@ts-ignore cannot find module error
        imgUrl={require('static/backgrounds/1323344124.jpg')}
        type="triangle"
        angleBottomDisplay="lg-none"
      >
        <h2 className="section-heading">More About What You’ll Discover</h2>
        <IconListCL>
          <>The truth about how much protein we really need</>
          <>Health benefits of going plant-based</>
          <>Quick, easy recipes for plant-based beginners</>
          <>How going plant-based benefits the environment</>
          <>Tips for making positive changes</>
          <>How to shop plant-based</>
          <>Supplements to consider</>
          <>Tips for creating plant-based meal plans</>
          <>The best foods for maximum plant-based nutrition</>
        </IconListCL>
      </SectionCL>

      <SectionCL
        id="offer-learn-2"
        color="light-gray"
        imgPosition="right"
        //@ts-ignore cannot find module error
        imgUrl={require('static/backgrounds/1286721496.jpg')}
        type="triangle"
        innerClass="sectionCL-padding-lg"
      >
        <IconListCL>
          <>How to enjoy your favorite foods (meat and dairy-free!)</>
          <>Nutritional foundations of going plant-based</>
          <>How to order plant-based at restaurants</>
          <>Common substitutes and converting favorite recipes</>
          <>Tips for families and picky eaters</>
          <>Easy cooking tips for beginners and advanced cooks alike</>
          <>Potential nutritional gaps plant-based eaters need to know about</>
          <>Eating plant-based on-the-go</>
          <>Making healthy habits stick</>
        </IconListCL>
      </SectionCL>

      <SectionCL id="offer-bonuses" color="white" type="triangle">
        <Container>
          <Row>
            <Col>
              <h2 className="section-heading text-uppercase text-center">Bonuses!</h2>
              <p className="mb-5">
                We’ve brought in four of the top experts in plant-based eating to give you the foundations in a fraction
                of the time. These interviews were among the most popular ever conducted by two million-copy bestselling
                author John Robbins for Food Revolution Summits. They’re here to help you create a life you’re so
                obsessed with that going back to your old way of doing things never crosses your mind. You’ll learn the
                biggest dos and don’ts of nutrition that will open your eyes and save you from making big mistakes — and
                you’ll get audio files as well as PDF transcripts to download so you can revisit the content whenever
                you’d like. Get ready to build the life and the health of your dreams.
              </p>
              {variant === 'ad' ? (
                <>
                  {bonusSessionAds.map((bonusItem, i) => (
                    <Fragment key={`bonusItem-${i}`}>
                      <Row className="align-items-center justify-content-center">
                        <Col lg="3" className="">
                          <img className="rounded-circle" src={bonusItem.image} />
                        </Col>
                        <Col lg="9">
                          <p className="text-uppercase mb-2">
                            <b>Bonus Session #{i + 1}</b>
                          </p>
                          <h4 className="mt-0 mb-2">{bonusItem.speaker}</h4>
                          <h5 className="mt-0 mb-3">{bonusItem.title}</h5>
                          <ReactMarkdown source={bonusItem.description} />
                        </Col>
                      </Row>
                      {i < 3 ? <hr className="border-color-black-25 my-5" /> : null}
                    </Fragment>
                  ))}
                </>
              ) : (
                <>
                  {bonusSession.map((bonusItem, i) => (
                    <Fragment key={`bonusItem-${i}`}>
                      <Row className="align-items-center justify-content-center">
                        <Col lg="3" className="">
                          <img className="rounded-circle" src={bonusItem.image} />
                        </Col>
                        <Col lg="9">
                          <p className="text-uppercase mb-2">
                            <b>Bonus Session #{i + 1}</b>
                          </p>
                          <h4 className="mt-0 mb-2">{bonusItem.speaker}</h4>
                          <h5 className="mt-0 mb-3">{bonusItem.title}</h5>
                          <ReactMarkdown source={bonusItem.description} />
                        </Col>
                      </Row>
                      {i < 3 ? <hr className="border-color-black-25 my-5" /> : null}
                    </Fragment>
                  ))}
                </>
              )}
            </Col>
          </Row>
        </Container>
      </SectionCL>

      <SectionSales
        id="offer-cta-2"
        sectionHeading={
          <>
            <h3 className="section-heading mb-2">
              Ready Now? <i>Join Us!</i>
            </h3>
            <h5 className="mt-0">Unlimited Access. Enjoy it all any time — for life!</h5>
          </>
        }
      />

      {
        {
          screening: (
            <SectionCL id="offer-special-enrollment" color="white" className="text-center" type="triangle">
              <Container>
                <Row>
                  <Col>
                    <h2 className="section-heading">About Today’s Special Early-Bird Enrollment Discount</h2>
                    <p>This early-bird discount is only for Masterclass attendees.</p>
                    <p>
                      *You can <i>only</i> unlock your special price on this page.*
                    </p>
                    <p className="balance-text">
                      <b>
                        With this special Masterclass price, you save $170 by enrolling in the{' '}
                        <i>Plant-Powered Playbook</i> today.
                      </b>
                    </p>
                    <p className="balance-text">
                      Order from this page to get the best offer possible. (Do not hit refresh or close the page!) The
                      enrollment price is higher everywhere else on our site.
                    </p>
                  </Col>
                </Row>
              </Container>
            </SectionCL>
          ),
          sales: null
        }[version]
      }

      <SectionCL id="offer-guarantee" color="light-gray" type="triangle">
        <Container>
          <Row>
            <Col>
              <GuaranteeBox title={<>You’re Protected By Our 60-Day, Unconditional, Money-Back Guarantee.</>}>
                <p>
                  {variant === 'ad' ? (
                    <>
                      Try it out. If you aren't completely blown away by the course, even if you just don’t like it,
                      just email us at <a href="mailto:support@foodrevolution.org">support@foodrevolution.org</a> any
                      time within 60 days after purchase to ask for a prompt and courteous refund. Take the whole
                      course, take every lesson, and make every single mouth-watering recipe, and if it doesn’t rock
                      your world, get every penny back. That’s how confident we are that you’ll LOVE the Plant-Powered
                      Playbook!
                    </>
                  ) : (
                    <>
                      Try it out. If you aren't completely blown away by the course, even if you just don’t like it,
                      just email us at <a href="mailto:support@foodrevolution.org">support@foodrevolution.org</a> any
                      time within 60 days of purchase to ask for a prompt and courteous refund. This means you can even
                      take the whole course, take every lesson, and make every single mouth-watering recipe, and if it
                      doesn’t rock your world, you get every penny back. That’s how confident we are that you’ll LOVE
                      the <i>Plant-Powered Playbook!</i>
                    </>
                  )}
                </p>
                <p>
                  PLUS, if for any reason you request a refund, the entire Playbook and everything that you’ve
                  downloaded is still yours to keep. That’s why we call our guarantee “better than money back.”
                </p>
              </GuaranteeBox>
            </Col>
          </Row>
        </Container>
      </SectionCL>

      <SectionCL id="offer-recap" color="white" type="triangle">
        <Container>
          <Row>
            <Col>
              <div className="d-flex justify-content-center">
                <div className="box-shadow-black-50 rounded">
                  <div className="background-black text-white p-4 rounded-top">
                    <h3 className="section-only-heading text-center">A Recap of What You’ll Get</h3>
                  </div>
                  <div className="background-white px-5 pt-4 pb-5 rounded-bottom">
                    <div className="text-center">
                      <ProductImage maxHeight="500" />
                    </div>
                    <div className="d-flex justify-content-center">
                      <IconListCL className="my-0">
                        <>
                          The 60-Page <i>Plant-Powered Playbook</i>
                        </>
                        <>15 Video Tutorials</>
                        <>8 Kitchen Demos</>
                        <>15 Plant-Based Recipes</>
                        <>Printable Shopping List</>
                        <>Plant-Based Meal Prep Guide</>
                        <>Links to Scientific Journals and Blog Articles</>
                        <>Bonus! 4 Interviews With Plant-Based Lifestyle Experts</>
                        <>
                          BONUS SESSION #1 – Dean Ornish, MD: <br className="d-none d-xl-block" />
                          The Transformative Power of Lifestyle Medicine
                        </>
                        <>
                          BONUS SESSION #2 – Michael Klaper, MD: <br className="d-none d-xl-block" />
                          The Science of Nutrition
                        </>
                        <>
                          BONUS SESSION #3 – Haile Thomas: <br className="d-none d-xl-block" />
                          Living Lively Through the Power of Plants
                        </>
                        <>
                          BONUS SESSION #4 – Brenda Davis, RD: <br className="d-none d-xl-block" />
                          What You Must Know About Nutrition
                        </>
                      </IconListCL>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </SectionCL>

      <SectionCL id="offer-faq" color="light-gray" type="triangle">
        <Container>
          <Row>
            <Col>
              <h2 className="section-heading mb-5">Frequently Asked Questions</h2>
              <FaqList faqlist={variant === 'ad' ? faqAds : faq} />
            </Col>
          </Row>
        </Container>
      </SectionCL>

      <SectionSales
        id="offer-cta-3"
        sectionHeading={
          <>
            <h5 className="section-heading">
              <i>
                {variant === 'ad' ? (
                  <>Ready to make a difference for health, animals, and the planet?</>
                ) : (
                  <>Ready to make a difference for your health, animals, and the planet?</>
                )}
              </i>
            </h5>
            <h2 className="text-uppercase mt-0 mb-2">Join Now!</h2>
          </>
        }
      />

      <SectionCL id="offer-references" color="light-gray" angle="none" type="triangle" order="last">
        <Container>
          <Row>
            <Col>
              <h4 className="section-heading">References</h4>
              <ListReferences>
                {(segment === 'sustainability' ? referencesSustainability : references).map((reference, i) => (
                  <ListReferenceItem
                    number={i + 1}
                    text={<ReactMarkdown source={reference.link} className="list-item-copy" skipHtml />}
                    key={`referenceItem-${i}`}
                  />
                ))}
              </ListReferences>
            </Col>
          </Row>
        </Container>
      </SectionCL>
    </>
  );
}
